
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import VPSPricingSection from '@/components/VPSPricingSection';
import SEOMetadata from '@/components/SEOMetadata';
import { Server, Zap, Shield, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const VPSServers = () => {
  const benefits = [
    {
      icon: Zap,
      title: "Instant Deployment",
      description: "VPS ready in minutes"
    },
    {
      icon: Shield,
      title: "Full Root Access",
      description: "Complete control over your virtual environment"
    },
    {
      icon: Clock,
      title: "24/7 Support",
      description: "Round-the-clock technical assistance"
    }
  ];

  const scrollToPackages = () => {
    const packagesSection = document.querySelector('.vps-packages-section');
    if (packagesSection) {
      const headerHeight = 80; // Account for fixed header
      const offset = packagesSection.getBoundingClientRect().top + window.pageYOffset - headerHeight;
      window.scrollTo({ 
        top: offset, 
        behavior: 'smooth' 
      });
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "VPS Servers",
    "provider": {
      "@type": "Organization",
      "name": "NEXX TELECOM"
    },
    "description": "Scalable Virtual Private Servers with full root access, instant deployment, and 24/7 support. Linux and Windows environments available.",
    "serviceType": "Virtual Private Server Hosting",
    "areaServed": "Global"
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOMetadata
        title="VPS Servers | Virtual Private Server Hosting | NEXX"
        description="Scalable VPS hosting with full root access and instant deployment. Linux and Windows environments available. 24/7 support and flexible resources."
        keywords="VPS servers, virtual private servers, scalable hosting, Linux VPS, Windows VPS, cloud servers, virtual hosting"
        canonical="https://nexx.net/vps-servers"
        structuredData={structuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-8 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        {/* Floating Server Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
          <div className="absolute top-16 left-12 opacity-5 animate-float-up" style={{ animationDuration: '8s' }}>
            <Server className="w-8 h-8 text-nexx-green" />
          </div>
          <div className="absolute top-32 right-16 opacity-5 animate-float-up" style={{ animationDuration: '10s', animationDelay: '2s' }}>
            <Server className="w-12 h-12 text-blue-500" />
          </div>
          <div className="absolute top-48 left-1/4 opacity-8 animate-float-up" style={{ animationDuration: '12s', animationDelay: '4s' }}>
            <Server className="w-6 h-6 text-gray-400" />
          </div>
          <div className="absolute top-60 right-1/3 opacity-5 animate-float-up" style={{ animationDuration: '9s', animationDelay: '1s' }}>
            <Server className="w-10 h-10 text-nexx-green" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 mt-8">
              Scalable <span className="text-nexx-green">Virtual Private Servers</span>
            </h1>
            <div className="text-2xl lg:text-3xl text-gray-600 mb-6 overflow-hidden relative">
              <span className="inline-block animate-grow-scale bg-gradient-to-r from-nexx-green to-blue-500 bg-clip-text text-transparent font-semibold">
                Flexible VPS hosting for every need.
              </span>
            </div>
            <p className="text-lg text-gray-500 mb-4 max-w-3xl mx-auto">
              Our VPS solutions offer the perfect balance of performance, control, and affordability. Get your virtual server up and running in minutes with full root access and 24/7 support.
            </p>
            <p className="text-lg text-gray-500 mb-10 max-w-3xl mx-auto">
              Choose from Linux or Windows environments with scalable resources to match your growing needs.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Button 
                size="lg" 
                className="bg-nexx-green hover:bg-green-700 text-white px-8 py-4 text-lg group"
                data-tally-open="3NQKvW" 
                data-tally-emoji-text="👋" 
                data-tally-emoji-animation="wave"
              >
                Get Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-nexx-green text-nexx-green hover:bg-nexx-green hover:text-white px-8 py-4 text-lg"
                onClick={scrollToPackages}
              >
                Order Now
              </Button>
              <Link to="/server-tos">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white px-8 py-4 text-lg"
                >
                  Read Our TOS
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-16">
            {benefits.map((benefit, index) => (
              <div key={benefit.title} className="text-center group">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-nexx-green group-hover:scale-110 transition-all duration-300">
                  <benefit.icon className="h-6 w-6 text-nexx-green group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">{benefit.title}</h3>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VPS Pricing Section */}
      <div className="vps-packages-section">
        <VPSPricingSection />
      </div>

      <Footer />
    </div>
  );
};

export default VPSServers;
